import { z } from 'zod';

import {
  idSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  score100Schema,
  score10Schema,
  sortOrderSchema,
  ulidSchema,
} from '../common';
import {
  agentCommandStatusSchema,
  dedupMatchReasonSchema,
  keywordMatchTypeSchema,
  platformSchema,
  postIntentSchema,
  postReviewStatusSchema,
  postSourceTypeSchema,
  recommendedActionSchema,
  sentimentSchema,
  urgencySchema,
} from '../enums';

/**
 * `/posts` DTOs.
 *
 * Scale discipline, restated because this is where it is easiest to break:
 * every score the system computes is on 0-100, and `finalScore10` is a derived
 * display value produced once at the end. Both are exposed here — the list item
 * carries `finalScore10` so the dashboard never divides anything itself, and
 * keeps the 0-100 components so a detail view can explain the number.
 *
 * A post row is global (one Facebook post = one row). Everything project-scoped
 * — review state, keyword matches, scores — hangs off the post *match*.
 */

/**
 * Query-string booleans.
 *
 * `z.coerce.boolean()` is wrong for this: it applies JavaScript truthiness, so
 * the string "false" would coerce to `true` and every negative filter would
 * silently invert.
 */
export const queryBooleanSchema = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((value) => value === true || value === 'true' || value === '1');
export type QueryBoolean = z.infer<typeof queryBooleanSchema>;

/** A filter accepting either one status or a repeated query parameter. */
export const postReviewStatusFilterSchema = z
  .union([postReviewStatusSchema, z.array(postReviewStatusSchema)])
  .transform((value) => (Array.isArray(value) ? value : [value]));

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

export const postAuthorSchema = z.object({
  externalAuthorId: z.string().nullable(),
  name: z.string().nullable(),
  profileUrl: z.string().url().nullable(),
  avatarUrl: z.string().url().nullable(),
  /** Author blocked by the operator for this workspace. */
  isBlocked: z.boolean(),
});
export type PostAuthor = z.infer<typeof postAuthorSchema>;

export const postSourceSchema = z.object({
  type: postSourceTypeSchema,
  name: z.string().nullable(),
  url: z.string().url().nullable(),
  externalSourceId: z.string().nullable(),
  isBlocked: z.boolean(),
});
export type PostSource = z.infer<typeof postSourceSchema>;

export const postKeywordMatchSchema = z.object({
  keywordId: idSchema,
  term: z.string(),
  matchType: keywordMatchTypeSchema,
  /** The literal text that matched, for highlighting. Null when the match came
   *  from a normalised form rather than a verbatim span. */
  matchedText: z.string().nullable(),
  /** Character offset; a match near the start of a post scores higher. */
  position: z.number().int().min(0).nullable(),
});
export type PostKeywordMatch = z.infer<typeof postKeywordMatchSchema>;

/**
 * One scoring revision. Re-scoring a post appends a revision instead of
 * overwriting, so a score change is always explainable after the fact —
 * `reason` is mandatory for exactly that purpose.
 */
export const postScoreSchema = z.object({
  id: idSchema,
  postMatchId: idSchema,
  revision: z.number().int().min(1),
  ruleScore100: score100Schema,
  aiScore100: score100Schema.nullable(),
  intentScore100: score100Schema.nullable(),
  freshnessScore100: score100Schema,
  finalScore100: score100Schema,
  /** Display value: `Math.round(finalScore100) / 10`. Never recomputed client-side. */
  finalScore10: score10Schema,
  reason: z.string(),
  intent: postIntentSchema.nullable(),
  purchaseLikelihood100: score100Schema.nullable(),
  urgency: urgencySchema.nullable(),
  sentiment: sentimentSchema.nullable(),
  recommendedAction: recommendedActionSchema.nullable(),
  matchedReasons: z.array(z.string()),
  riskFlags: z.array(z.string()),
  /**
   * Did the AI judge this post to be asking for what the project's keywords
   * describe? THREE-valued on purpose:
   *   true  — yes, a lead the project can answer;
   *   false — no, whatever the keyword overlap says;
   *   null  — nobody has asked yet (the revision never reached the AI tier).
   * "Chưa hỏi" is not "không đúng ý", and a filter must not conflate them.
   * Defaulted so a response from an older server still parses.
   */
  matchesSearchIntent: z.boolean().nullable().default(null),
  /** One short Vietnamese sentence behind the verdict above. */
  matchesIntentReason: z.string().nullable().default(null),
  /** True when an operator overrode the computed score by hand. */
  isManualOverride: z.boolean(),
  scoredAt: isoDateTimeSchema,
});
export type PostScore = z.infer<typeof postScoreSchema>;

// ---------------------------------------------------------------------------
// GET /posts
// ---------------------------------------------------------------------------

export const POST_SORT_FIELDS = [
  'finalScore',
  'postedAt',
  'discoveredAt',
  'reactionCount',
  'commentCount',
] as const;
export const postSortFieldSchema = z.enum(POST_SORT_FIELDS);
export type PostSortField = (typeof POST_SORT_FIELDS)[number];

export const postListQuerySchema = paginationQuerySchema.extend({
  projectId: idSchema.optional(),
  platform: platformSchema.optional(),
  keywordId: idSchema.optional(),
  /**
   * Minimum relevance on the **0.0-10.0 display scale** the user sees in the
   * UI. The server multiplies by 10 before touching `final_score_100`; the
   * conversion happens in exactly one place so the two scales cannot drift.
   */
  minScore: z.coerce.number().min(0).max(10).optional(),
  maxScore: z.coerce.number().min(0).max(10).optional(),
  /** Filter on `postedAt` when present, else on `discoveredAt`. */
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  sourceType: z.union([postSourceTypeSchema, z.array(postSourceTypeSchema)]).optional(),
  hasComment: queryBooleanSchema.optional(),
  hasPurchaseIntent: queryBooleanSchema.optional(),
  hasError: queryBooleanSchema.optional(),
  reviewStatus: postReviewStatusFilterSchema.optional(),
  /** Shorthand for reviewStatus NEW (`false`) versus anything else (`true`). */
  seen: queryBooleanSchema.optional(),
  intent: z.union([postIntentSchema, z.array(postIntentSchema)]).optional(),
  /**
   * Keep only posts the AI judged to be asking for what this project sells.
   *
   * `true` means exactly that — NOT "everything except false": a post nobody
   * asked about is not evidence of a match, and including it would rebuild the
   * noisy list this filter exists to escape.
   */
  matchesSearchIntent: queryBooleanSchema.optional(),
  /** Free-text search over post content and author name. */
  search: z.string().trim().max(200).optional(),
  sortBy: postSortFieldSchema.default('finalScore'),
  sortOrder: sortOrderSchema,
});
export type PostListQuery = z.infer<typeof postListQuerySchema>;

export const postListItemSchema = z.object({
  /** Global post id. */
  postId: idSchema,
  /** Project-scoped join; every mutation in this file addresses this id. */
  postMatchId: idSchema,
  projectId: idSchema,
  platform: platformSchema,
  externalPostId: z.string().nullable(),
  permalink: z.string().url().nullable(),
  /** Truncated for the list; the full body is on the detail response. */
  contentExcerpt: z.string(),
  contentLength: z.number().int().min(0),
  author: postAuthorSchema,
  source: postSourceSchema,
  postedAt: isoDateTimeSchema.nullable(),
  /** Raw label shown when `postedAt` could not be parsed; display-only. */
  postedAtText: z.string().nullable(),
  firstDiscoveredAt: isoDateTimeSchema,
  lastDiscoveredAt: isoDateTimeSchema,
  reactionCount: z.number().int().min(0).nullable(),
  commentCount: z.number().int().min(0).nullable(),
  shareCount: z.number().int().min(0).nullable(),
  mediaCount: z.number().int().min(0),
  /** Display score, 0.0-10.0 — the only score the list view renders. */
  finalScore10: score10Schema.nullable(),
  /** Internal 0-100 values kept alongside so a tooltip can break down the
   *  number without a second request. */
  finalScore100: score100Schema.nullable(),
  ruleScore100: score100Schema.nullable(),
  aiScore100: score100Schema.nullable(),
  intent: postIntentSchema.nullable(),
  purchaseLikelihood100: score100Schema.nullable(),
  /**
   * The AI's yes/no on "is this person asking for what this project sells", from
   * the newest scoring revision. `null` = never asked (rule tier only).
   * Defaulted so a response from an older server still parses.
   */
  matchesSearchIntent: z.boolean().nullable().default(null),
  matchesIntentReason: z.string().nullable().default(null),
  reviewStatus: postReviewStatusSchema,
  isSeen: z.boolean(),
  hasCommentJob: z.boolean(),
  hasError: z.boolean(),
  errorMessage: z.string().nullable(),
  keywordMatches: z.array(postKeywordMatchSchema),
});
export type PostListItem = z.infer<typeof postListItemSchema>;

export const postListResponseSchema = paginated(postListItemSchema);
export type PostListResponse = z.infer<typeof postListResponseSchema>;

// ---------------------------------------------------------------------------
// GET /posts/:id
// ---------------------------------------------------------------------------

export const postMediaSchema = z.object({
  id: idSchema,
  /** MVP stores the Facebook URL and metadata only; media is not downloaded. */
  url: z.string().url(),
  thumbnailUrl: z.string().url().nullable(),
  mimeType: z.string().nullable(),
  width: z.number().int().min(0).nullable(),
  height: z.number().int().min(0).nullable(),
});
export type PostMedia = z.infer<typeof postMediaSchema>;

export const postDetailSchema = z.object({
  id: idSchema,
  platform: platformSchema,
  externalPostId: z.string().nullable(),
  permalink: z.string().url().nullable(),
  content: z.string(),
  author: postAuthorSchema,
  source: postSourceSchema,
  postedAt: isoDateTimeSchema.nullable(),
  /** Raw label shown when `postedAt` could not be parsed; display-only. */
  postedAtText: z.string().nullable(),
  reactionCount: z.number().int().min(0).nullable(),
  commentCount: z.number().int().min(0).nullable(),
  shareCount: z.number().int().min(0).nullable(),
  media: z.array(postMediaSchema),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type PostDetail = z.infer<typeof postDetailSchema>;

export const postMatchSchema = z.object({
  id: idSchema,
  postId: idSchema,
  projectId: idSchema,
  firstDiscoveredAt: isoDateTimeSchema,
  lastDiscoveredAt: isoDateTimeSchema,
  /** Which dedup rule recognised this post, kept so ingestion stays explainable. */
  dedupMatchReason: dedupMatchReasonSchema,
  reviewStatus: postReviewStatusSchema,
  reviewedAt: isoDateTimeSchema.nullable(),
  reviewedByUserId: idSchema.nullable(),
  isSeen: z.boolean(),
  notes: z.string().nullable(),
});
export type PostMatch = z.infer<typeof postMatchSchema>;

export const postDetailResponseSchema = z.object({
  post: postDetailSchema,
  match: postMatchSchema,
  latestScore: postScoreSchema.nullable(),
  /** Newest first; every re-score appends one entry. */
  scoreHistory: z.array(postScoreSchema),
  keywordMatches: z.array(postKeywordMatchSchema),
  /** Comment jobs raised against this post, newest first. */
  commentJobIds: z.array(idSchema),
});
export type PostDetailResponse = z.infer<typeof postDetailResponseSchema>;

// ---------------------------------------------------------------------------
// PATCH /posts/:id/review
// ---------------------------------------------------------------------------

export const postReviewRequestSchema = z
  .object({
    projectId: idSchema,
    /** VIEWED / RELEVANT / IRRELEVANT / BLOCKED / ARCHIVED. */
    reviewStatus: postReviewStatusSchema.optional(),
    markSeen: z.boolean().optional(),
    /**
     * Operator override on the display scale. Stored as a new score revision
     * with `isManualOverride`, never by editing the computed revision — the
     * original number stays auditable.
     */
    manualScore10: score10Schema.optional(),
    manualScoreReason: z.string().trim().max(500).optional(),
    /** Blocks every future post by this author across the workspace. */
    blockAuthor: z.boolean().optional(),
    blockSource: z.boolean().optional(),
    notes: z.string().trim().max(2_000).optional(),
  })
  .refine(
    (value) =>
      value.reviewStatus !== undefined ||
      value.markSeen !== undefined ||
      value.manualScore10 !== undefined ||
      value.blockAuthor !== undefined ||
      value.blockSource !== undefined ||
      value.notes !== undefined,
    { message: 'Cần ít nhất một hành động review' },
  )
  .refine((value) => value.manualScore10 === undefined || Boolean(value.manualScoreReason), {
    message: 'Chỉnh điểm thủ công phải kèm lý do',
    path: ['manualScoreReason'],
  });
export type PostReviewRequest = z.infer<typeof postReviewRequestSchema>;

export const postReviewResponseSchema = z.object({
  postMatchId: idSchema,
  reviewStatus: postReviewStatusSchema,
  isSeen: z.boolean(),
  latestScore: postScoreSchema.nullable(),
  authorBlocked: z.boolean(),
  sourceBlocked: z.boolean(),
  updatedAt: isoDateTimeSchema,
});
export type PostReviewResponse = z.infer<typeof postReviewResponseSchema>;

// ---------------------------------------------------------------------------
// POST /posts/:id/open
// ---------------------------------------------------------------------------

/**
 * Opens the post in the agent's Chromium.
 *
 * The client names a post, never a URL: the server resolves the permalink from
 * the stored post row before issuing the command. A caller-supplied URL would
 * turn this endpoint into "navigate that machine anywhere", which is precisely
 * what the closed command allowlist exists to prevent.
 */
export const openPostRequestSchema = z.object({
  projectId: idSchema.optional(),
  browserProfileId: idSchema.optional(),
  bringToFront: z.boolean().default(true),
});
export type OpenPostRequest = z.infer<typeof openPostRequestSchema>;

export const openPostResponseSchema = z.object({
  commandId: ulidSchema,
  /** Reuses the interactive-window command from the allowlist; opening a post
   *  is not a new capability, just a headed window with a resolved target. */
  type: z.literal('browser.open_interactive'),
  status: agentCommandStatusSchema,
  agentId: idSchema,
  browserProfileId: idSchema,
  /** Echo of the permalink the server resolved, so the UI can show it. */
  resolvedUrl: z.string().url(),
  issuedAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
});
export type OpenPostResponse = z.infer<typeof openPostResponseSchema>;

// ---------------------------------------------------------------------------
// DELETE /posts/:postMatchId
// ---------------------------------------------------------------------------

/**
 * Deleting is data cleanup, not suppression: the match row (and its scores,
 * keyword matches and comment jobs, via cascade) goes away, but the next search
 * cycle will re-create it if the post still matches a keyword. "Never show me
 * this again" is what blocking the author/source is for.
 */
export const deletePostResponseSchema = z.object({
  postMatchId: idSchema,
  /** True when the global posts row was also cleaned up because no other
   *  project's match references it anymore. */
  postDeleted: z.boolean(),
  deletedAt: isoDateTimeSchema,
});
export type DeletePostResponse = z.infer<typeof deletePostResponseSchema>;

// ---------------------------------------------------------------------------
// POST /posts/bulk-delete
// ---------------------------------------------------------------------------

/**
 * A POST, not a DELETE: the dashboard's `apiDelete` takes no body or params,
 * and DELETE-with-body is ambiguous enough that proxies drop it.
 *
 * The body is EXACTLY the filter set of `GET /posts` minus pagination/sorting,
 * so "delete everything matching the current filters" and "the list I am
 * looking at" cannot drift apart — there is no second translation of the
 * filters to get out of sync. `projectId` is mandatory: a bulk delete must
 * never cross projects by omission.
 */
export const bulkDeletePostsRequestSchema = postListQuerySchema
  .omit({ page: true, pageSize: true, sortBy: true, sortOrder: true, hasError: true })
  .extend({ projectId: idSchema });
export type BulkDeletePostsRequest = z.infer<typeof bulkDeletePostsRequestSchema>;

export const bulkDeletePostsResponseSchema = z.object({
  deletedMatches: z.number().int().min(0),
  /** Global posts rows cleaned up because deleting the matches orphaned them. */
  deletedPosts: z.number().int().min(0),
  deletedAt: isoDateTimeSchema,
});
export type BulkDeletePostsResponse = z.infer<typeof bulkDeletePostsResponseSchema>;
